/**
 * FormObserver Component
 *
 * A utility component for observing changes in form values and synchronizing them with a parent state.
 * Typically used with Formik or other form libraries to capture live updates of form values.
 *
 * Props:
 * @param {object} values - The current values of the form fields.
 * @param {(values: object) => void} setInitialValues - Callback function to update the parent or external state with the latest form values.
 *
 * Behavior:
 * - Listens for changes in the `values` prop.
 * - Calls `setInitialValues` whenever `values` changes.
 * - Returns null as it does not render any visible UI.
 *
 * Usage:
 * ```tsx
 * <FormObserver
 *   values={formik.values}
 *   setInitialValues={(vals) => setParentState(vals)}
 * />
 * ```
 *
 * Notes:
 * - Useful for keeping external state in sync with a form without rendering additional elements.
 * - Minimal overhead as it only leverages `useEffect` for observing changes.
 */

import { useEffect } from "react";
import { FormObserverProps } from "../../types/GeneralComponents";
const FormObserver: React.FC<FormObserverProps> = ({
  values,
  setInitialValues,
}) => {
  useEffect(() => {
    setInitialValues(values);
  }, [values, setInitialValues]);

  return null;
};

export default FormObserver;
